"use client";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";

const API_URL = REST_API_URL;

export default function ByWorkers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userType, setUserType] = useState(null);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    if (!token) navigate("/sign-in");
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;

    const fetchUserAndWorkers = async () => {
      try {
        setLoading(true);
        setError(null);

        const headers = buildHeaders();

        const userRes = await fetch(`${API_URL}/auth/userinfo/`, { headers });
        const userData = await userRes.json();
        const type = userData?.user?.status || null;
        setUserType(type);

        if (type !== "salon") {
          setLoading(false);
          return;
        }

        const res = await fetch(`${API_URL}/auth/workers-list/`, { headers });

        if (res.status === 401) {
          localStorage.clear();
          sessionStorage.clear();
          navigate("/sign-in");
          return;
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setWorkers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("❌ Ошибка при загрузке работников:", err);
        setError(t("home.errorFetchFailedLoadWorkers"));
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndWorkers();
  }, [token, navigate, t]);

  if (userType !== "salon") return null;

  const activeWorkers = workers.filter(
    (worker) => worker.full_name && worker.full_name.trim() !== "",
  );
  const unregisteredWorkers = workers.filter(
    (worker) => !worker.full_name || worker.full_name.trim() === "",
  );

  return (
    <div className="flex flex-col bg-white rounded-2xl shadow-md p-6 w-full h-[343px]">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[#302B2B] text-2xl font-bold">
          {t("home.byWorkersTitle")}
        </h2>
      </div>

      <div className="flex justify-between text-[#302B2B] text-[18px] font-normal mb-2 px-1">
        <span>{t("home.workerTableHeader")}</span>
        <span>{t("home.servicesTableHeader")}</span>
      </div>

      <div className="h-[1px] bg-[#EDEEF3] mb-4"></div>

      {loading ? (
        <p className="text-neutral-400 text-sm text-center">
          {t("home.loadingText")}
        </p>
      ) : error ? (
        <p className="text-red-500 text-sm italic text-center">{error}</p>
      ) : workers.length === 0 ? (
        <p className="text-[#9A99AB] text-sm text-center">
          {t("home.noWorkersData")}
        </p>
      ) : (
        <div className="flex flex-col gap-5 overflow-y-auto max-h-[280px] pr-[20px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
          {/* Активные работники */}
          {activeWorkers.map((worker) => (
            <div
              key={worker.id}
              className="flex justify-between items-center w-full"
            >
              <div className="flex items-center gap-3">
                <img
                  src={
                    worker.avatar ||
                    "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                  }
                  alt={worker.full_name}
                  className="w-11 h-11 rounded-full object-cover"
                />
                <div className="flex flex-col">
                  <span className="text-[#302B2B] text-[18px] font-normal">
                    {worker.full_name}
                  </span>
                  <span className="text-[#9A99AB] text-[14px]">
                    {worker.worker_email || "—"}
                  </span>
                </div>
              </div>
              <span className="text-[#302B2B] text-[18px]">
                {worker.services ?? 0}
              </span>
            </div>
          ))}

          {unregisteredWorkers.length > 0 && (
            <>
              <div className="h-[1px] bg-[#EDEEF3] my-2"></div>
              {unregisteredWorkers.map((worker) => (
                <div
                  key={worker.id}
                  className="flex justify-between items-center w-full opacity-60 cursor-not-allowed"
                >
                  <div className="flex flex-col">
                    <span className="text-[#9A99AB] text-[16px]">
                      {t("home.unregisteredUserLabel")}
                    </span>
                    <span className="text-[#9A99AB] text-[14px]">
                      {worker.worker_email || "—"}
                    </span>
                  </div>
                  <span className="text-[#302B2B] text-[18px]">
                    {worker.services ?? 0}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
